/* eslint-disable @typescript-eslint/no-misused-promises */
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import {
  ApplyOpportunityFormPart1Schema,
  applyOpportunityFormPart1Schema,
  applyOpportunityFormPart2Schema,
  ApplyOpportunityFormPart2Schema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CurrencyInput, Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PopoverTooltip } from "@/components/ui/tooltip";
import {
  Check,
  CircleAlert,
  Clipboard,
  ListChecks,
  NotepadText,
  Users,
} from "lucide-react";

import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import formatBudgetRange from "@/utils/formatBudgetRange";

import useApplyForOpportunityMutation from "./mutations";

import {
  CandidateJobOpening,
  candidateOpportunityHourlyRateList,
  candidateOpportunityMonthlyRetainerList,
  IconProp,
} from "@/types";

const MAX_QUOTE_CHARACTERS = 300;

interface BaseProps {
  opening: CandidateJobOpening;
}

// Parent may directly control dialog open state, but does not have to
type ControlProps =
  | {
      button?: React.ReactNode;
      open?: never;
      setOpen?: never;
    }
  | {
      button?: never;
      open: boolean;
      setOpen: (v: boolean) => void;
    };

type Props = BaseProps & ControlProps;

const OpportunityApplyDialog = ({
  opening,
  button,
  open: externalOpen,
  setOpen: setExternalOpen,
}: Props) => {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const [step, setStep] = useState<"fit" | "why" | "done">("fit");

  // If parent is not explicitly controlling dialog state, it is controlled internally
  const [open, setOpen] =
    externalOpen !== undefined && setExternalOpen
      ? [externalOpen, setExternalOpen]
      : [internalOpen, setInternalOpen];

  const {
    mutate,
    isPending: mutationLoading,
    isError: mutationError,
  } = useApplyForOpportunityMutation(opening.id, () => setStep("done"));

  const part1Form = useForm<ApplyOpportunityFormPart1Schema>({
    resolver: async (data, context, opts) => {
      return zodResolver(applyOpportunityFormPart1Schema)(data, context, opts);
    },
    defaultValues: {
      // If the role is full-time, don't ask about scope
      agreesWithScope: opening.type === "Full-time" || undefined,
      // If a start date is not provided, then implicitly disagree with it
      agreesWithStartDate: Boolean(opening.desiredStartDate) && undefined,
      agreesWithPay:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        Boolean(
          opening.budgetMinCents ??
            opening?.budgetMaxCents ??
            opening?.budgetNotes,
        ) && undefined,
    },
  });

  const part2Form = useForm<ApplyOpportunityFormPart2Schema>({
    resolver: async (data, context, opts) => {
      return zodResolver(applyOpportunityFormPart2Schema)(data, context, opts);
    },
  });

  async function onSubmit() {
    if (mutationLoading) return;

    const part1Valid = await part1Form.trigger();
    1;
    const part2Valid = await part2Form.trigger();

    if (part1Valid && part2Valid) {
      const part1Data = part1Form.getValues();
      const part2Data = part2Form.getValues();

      const fullData = {
        ...part1Data,
        ...part2Data,
      };

      if (part1Data.budgetMinCents && part1Data.budgetMaxCents) {
        fullData.budgetType = "Yearly salary";
      }

      mutate(fullData);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {externalOpen === undefined && !setExternalOpen ? (
        <DialogTrigger asChild>
          {button ?? (
            <Button variant="outline">
              <Clipboard />
              Apply
            </Button>
          )}
        </DialogTrigger>
      ) : null}
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {opening.company?.name
                ? `Applying to ${opening.company?.name}`
                : "Applying to Opportunity"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Apply for an opportunity
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          {mutationError && (
            <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
              Something went wrong when applying for the job opening, please try
              again later.
            </p>
          )}
          {step === "fit" ? (
            <Part1FormContent opening={opening} form={part1Form} />
          ) : step === "why" ? (
            <Part2FormContent opening={opening} form={part2Form} />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-6">
              <Check
                className="rounded-full bg-[#DCFCE7]"
                size={128}
                stroke="#1E7667"
              />
              <div className="flex flex-col gap-1 items-stretch text-center">
                <p className="text-lg font-semibold tracking-[-0.18px] text-[#22234A]">
                  You have successfully applied for this position
                </p>
                <p className="text-sm font-normal text-[#4B5563]">
                  Your application has been sent. We&apos;ll notify you if the
                  company expresses interest.
                </p>
              </div>
              <div className="p-4 flex flex-col gap-2 self-stretch rounded-lg bg-[#f9fafb]">
                <p className="text-base font-medium text-[#111827]">
                  What happens next?
                </p>
                <ul className="list-disc pl-5">
                  <li className="text-sm font-normal text-[#4b5563]">
                    The company will review your application
                  </li>
                  <li className="text-sm font-normal text-[#4b5563]">
                    You&apos;ll receive a notification if they&apos;re
                    interested
                  </li>
                  <li className="text-sm font-normal text-[#4b5563]">
                    You can then start a conversation with them
                  </li>
                  <li className="text-sm font-normal text-[#4b5563]">
                    Schedule meetings directly through the platform
                  </li>
                </ul>
              </div>
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {step === "done" ? "Close" : "Cancel"}
          </Button>
          {step === "fit" ? (
            <Button
              disabled={!part1Form.formState.isValid}
              variant="secondary"
              onClick={part1Form.handleSubmit(() => setStep("why"))}
            >
              Next
            </Button>
          ) : step === "why" ? (
            <Button
              disabled={!part2Form.formState.isValid}
              variant="secondary"
              onClick={part2Form.handleSubmit(onSubmit)}
            >
              Submit
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Part1FormContent = ({
  opening,
  form,
}: {
  opening: CandidateJobOpening;
  form: UseFormReturn<ApplyOpportunityFormPart1Schema>;
}) => {
  const { agreesWithScope, agreesWithStartDate, agreesWithPay } = form.watch();

  const formattedBudget =
    formatBudgetRange(opening.budgetMinCents, opening.budgetMaxCents) ??
    opening.budgetNotes;

  let budgetString = "Unknown";
  if (!formattedBudget) {
    // No-op
  } else if (opening.type === "Project" && opening.budgetType) {
    budgetString = `${formattedBudget} ${opening.budgetType === "Monthly retainer" ? "(monthly)" : "(project)"}`;
  } else if (opening.type === "Fractional") {
    budgetString = `${formattedBudget} (monthly)`;
  } else if (opening.type === "Full-time") {
    budgetString = `${formattedBudget} (salary)`;
  }

  const minError = form.formState.errors.budgetMinCents?.message;
  const maxError = form.formState.errors.budgetMaxCents?.message;

  const budgetError = maxError ?? minError;

  return (
    <Form {...form}>
      <form className="space-y-4 w-full">
        <div className="flex flex-col gap-1 self-stretch items-center">
          <h2 className="text-lg font-semibold text-[#22234A]">
            Confirm Opportunity Fit
          </h2>
          <p className="text-sm font-medium text-[#4E4F6C]">
            Please confirm the proposed terms of the engagement
          </p>
        </div>
        {opening.type !== "Full-time" && (
          <FormField
            control={form.control}
            name="agreesWithScope"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>
                  {opening.scopeTime && opening.scopeEngagementDuration
                    ? `The proposed scope for this role is ${opening.scopeTime} with a duration of ${opening.scopeEngagementDuration}`
                    : /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */
                      `The proposed scope for this role is ${opening.scopeTime || opening.scopeEngagementDuration}`}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value?.toString() ?? undefined}
                    onValueChange={(value) => field.onChange(value === "true")}
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <RadioGroupItem value="true">Yes, agree</RadioGroupItem>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <RadioGroupItem value="false">
                        Propose alternative
                      </RadioGroupItem>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {agreesWithScope === false && (
          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Propose your alternative</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {Boolean(opening.desiredStartDate) && (
          <FormField
            control={form.control}
            name="agreesWithStartDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>
                  {`Are you available to start on ${
                    dateStringToLocalDate(
                      opening.desiredStartDate,
                    )?.toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }) ?? "Unknown"
                  }?`}
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value?.toString() ?? undefined}
                    onValueChange={(value) => field.onChange(value === "true")}
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <RadioGroupItem value="true">
                        Yes, I am available
                      </RadioGroupItem>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <RadioGroupItem value="false">
                        Propose alternative
                      </RadioGroupItem>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {agreesWithStartDate === false && (
          <FormField
            control={form.control}
            name="earliestStartDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>
                  {opening.desiredStartDate
                    ? "Propose your alternative"
                    : "What is the earliest date you can start?"}
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    modal={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="agreesWithPay"
          render={({ field }) => (
            <FormItem>
              <FormLabel
                subheading={
                  opening.budgetNotes && opening.budgetNotes !== formattedBudget
                    ? `Note from company: ${opening.budgetNotes}`
                    : undefined
                }
                required
              >
                {`The proposed compensation for this role is ${budgetString}`}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value?.toString() ?? undefined}
                  onValueChange={(value) => field.onChange(value === "true")}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <RadioGroupItem value="true">Yes, agree</RadioGroupItem>
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <RadioGroupItem value="false">
                      Propose alternative
                    </RadioGroupItem>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {agreesWithPay === false ? (
          opening.type === "Fractional" ? (
            <>
              <FormField
                control={form.control}
                name="budgetHourly"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      What are your compensation expectations for the role
                      (hourly rate)?
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {candidateOpportunityHourlyRateList.map(
                            (retainer) => (
                              <SelectItem
                                key={`hourly-rate-${retainer}`}
                                value={retainer}
                              >
                                {retainer}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="budgetMonthly"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      <span className="flex flex-row gap-0.5">
                        What are your compensation expectations for the role
                        (monthly retainer)?
                        <PopoverTooltip content="Providing both rates helps companies assess budget flexibility in case the scope or hours of the role shift over time.">
                          <CircleAlert className="cursor-pointer" size={12} />
                        </PopoverTooltip>
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {candidateOpportunityMonthlyRetainerList.map(
                            (retainer) => (
                              <SelectItem
                                key={`monthly-retainer-${retainer}`}
                                value={retainer}
                              >
                                {retainer}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : opening.type === "Project" ? (
            <>
              <FormField
                control={form.control}
                name="budgetType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      What type of compensation are you proposing?
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            variant="default"
                            value="Fixed project budget"
                          >
                            Fixed project budget
                          </SelectItem>
                          <SelectItem
                            variant="default"
                            value="Monthly retainer"
                          >
                            Monthly retainer
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="budgetMonthly"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      <span className="flex flex-row gap-0.5">
                        What are your compensation expectations for the role?
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          {candidateOpportunityMonthlyRetainerList.map(
                            (retainer) => (
                              <SelectItem
                                key={`monthly-retainer-${retainer}`}
                                value={retainer}
                              >
                                {retainer}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : (
            <div className="flex flex-col gap-1 self-stretch">
              <FormLabel required>
                What are your salary expectations for the role?
              </FormLabel>
              <div className="flex flex-row gap-6 self-stretch items-center">
                <div className="flex flex-row gap-1 self-stretch items-center">
                  <FormField
                    control={form.control}
                    name="budgetMinCents"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <CurrencyInput
                            valueCents={field.value}
                            onValueCentsChange={field.onChange}
                            minCents={0}
                            placeholder="$75,000"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <span className="select-none">-</span>
                  <FormField
                    control={form.control}
                    name="budgetMaxCents"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <CurrencyInput
                            valueCents={field.value}
                            onValueCentsChange={field.onChange}
                            minCents={0}
                            placeholder="$100,000"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {budgetError ? (
                <p className="text-sm font-medium text-destructive">
                  {String(budgetError)}
                </p>
              ) : null}
            </div>
          )
        ) : undefined}
      </form>
    </Form>
  );
};

const Part2FormContent = ({
  opening,
  form,
}: {
  opening: CandidateJobOpening;
  form: UseFormReturn<ApplyOpportunityFormPart2Schema>;
}) => {
  return (
    <Form {...form}>
      <form className="space-y-4 w-full">
        <div className="flex flex-col gap-1 self-stretch items-center">
          <h2 className="text-lg font-semibold text-[#22234A]">
            Why You&apos;re a Great Match
          </h2>
          <p className="text-sm font-medium text-[#4E4F6C]">
            Bring your match to life! Use this space to share why you&apos;re a
            great fit in your own words—be confident, conversational, and
            specific.
          </p>
        </div>
        <MatchCriteriaInput
          Icon={ListChecks}
          criteria={opening.matchCriteria1}
          venturousResponse={opening.candidateStatus?.matchCriteria1}
          quoteField="matchCriteria1Quote"
          responseSuggestionField="matchCriteria1Suggestion"
          form={form}
        />
        <MatchCriteriaInput
          Icon={Users}
          criteria={opening.matchCriteria2}
          venturousResponse={opening.candidateStatus?.matchCriteria2}
          quoteField="matchCriteria2Quote"
          responseSuggestionField="matchCriteria2Suggestion"
          form={form}
        />
        <MatchCriteriaInput
          Icon={NotepadText}
          criteria={opening.matchCriteria3}
          venturousResponse={opening.candidateStatus?.matchCriteria3}
          quoteField="matchCriteria3Quote"
          responseSuggestionField="matchCriteria3Suggestion"
          form={form}
        />
      </form>
    </Form>
  );
};

const MatchCriteriaInput = ({
  Icon,
  criteria,
  venturousResponse,
  quoteField,
  responseSuggestionField,
  form,
}: {
  Icon: IconProp;
  criteria?: string;
  venturousResponse?: string;
  quoteField:
    | "matchCriteria1Quote"
    | "matchCriteria2Quote"
    | "matchCriteria3Quote";
  responseSuggestionField:
    | "matchCriteria1Suggestion"
    | "matchCriteria2Suggestion"
    | "matchCriteria3Suggestion";
  form: UseFormReturn<ApplyOpportunityFormPart2Schema>;
}) => {
  const [editResponse, setEditResponse] = useState(false);

  return (
    <div className="p-3 flex flex-col gap-3 self-stretch bg-[#FDF5F3] rounded-xl">
      <div className="flex flex-col gap-[2px] self-stretch">
        <div className="flex gap-2 self-stretch items-center">
          <Icon size={16} className="flex-shrink-0" stroke="#D23B15" />
          <h3 className="text-base font-semibold text-[#22234A]">{criteria}</h3>
        </div>
        {editResponse ? (
          <FormField
            control={form.control}
            name={responseSuggestionField}
            render={({ field }) => (
              <FormItem>
                <FormLabel hoverContent="If approved, this revised context will be shown instead of what was originally written.">
                  Your Suggestion
                </FormLabel>
                <p className="text-xs font-medium text-[#727E94]">{`${field.value?.length ?? 0}/${MAX_QUOTE_CHARACTERS}`}</p>
                <FormControl>
                  <Textarea
                    {...field}
                    onChange={(evt) => {
                      const value = evt.target.value;
                      if (value.length > MAX_QUOTE_CHARACTERS) {
                        field.onChange(
                          value.substring(0, MAX_QUOTE_CHARACTERS),
                        );
                      } else {
                        field.onChange(value);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : venturousResponse ? (
          <>
            <p className="text-sm font-medium text-[#4E4F6C]">
              {venturousResponse}
            </p>
            <p
              className="edit-text"
              onClick={() => {
                form.setValue(responseSuggestionField, venturousResponse);
                setEditResponse(true);
              }}
            >
              Request edit
            </p>
          </>
        ) : undefined}
      </div>
      <FormField
        control={form.control}
        name={quoteField}
        render={({ field }) => (
          <FormItem>
            <FormLabel
              hoverContent={
                venturousResponse
                  ? "We will display the above information to the client, paired with the context you provide in the below quote."
                  : "Share things about your background that make you a great fit for the role."
              }
              required
            >
              Tell us how you fit this key area
            </FormLabel>
            <p className="text-xs font-medium text-[#727E94]">{`${field.value?.length ?? 0}/${MAX_QUOTE_CHARACTERS}`}</p>
            <FormControl>
              <Textarea
                {...field}
                onChange={(evt) => {
                  const value = evt.target.value;
                  if (value.length > MAX_QUOTE_CHARACTERS) {
                    field.onChange(value.substring(0, MAX_QUOTE_CHARACTERS));
                  } else {
                    field.onChange(value);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default OpportunityApplyDialog;
